import { createClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG, validateSupabaseConfig } from './supabaseConfig';

// Supabase 환경변수 검증
if (!validateSupabaseConfig()) {
  console.warn('Supabase 설정이 올바르지 않습니다. 환경변수를 확인하세요.');
  console.warn('env.example 파일을 참고하여 .env 파일을 생성하세요.');
}

// Supabase 클라이언트 생성
export const supabase = createClient(SUPABASE_CONFIG.url, SUPABASE_CONFIG.anonKey);

// Service Role Key를 사용하는 클라이언트 (admin 사용자 생성용)
export const supabaseAdmin = createClient(
  SUPABASE_CONFIG.url, 
  process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY || SUPABASE_CONFIG.anonKey
);

// Supabase 테이블명 상수
export const TABLES = {
  ACCOUNTS: 'accounts',
  LOCKUP: 'lockup',
  ADMIN: 'admin',
  ACTIVITY_LOGS: 'activity_logs'
};

// Supabase API 함수들
export const supabaseAPI = {
  // 계정 관련 API
  getAccounts: async (page = 1, limit = 15) => {
    const { data, error, count } = await supabase
      .from(TABLES.ACCOUNTS)
      .select('*', { count: 'exact' })
      .range((page - 1) * limit, page * limit - 1)
      .order('inserted_at', { ascending: false });
    
    if (error) throw error;
    return { data, count };
  },

  getAccountByAddress: async (address) => {
    const { data, error } = await supabase
      .from(TABLES.ACCOUNTS)
      .select('*')
      .eq('address', address)
      .single();
    
    if (error) throw error;
    return data;
  },

  // 계정 검색 API
  searchAccounts: async (searchTerm, page = 1, limit = 15) => {
    const { data, error, count } = await supabase
      .from(TABLES.ACCOUNTS)
      .select('*', { count: 'exact' })
      .or(`address.ilike.%${searchTerm}%,holder_name.ilike.%${searchTerm}%`)
      .range((page - 1) * limit, page * limit - 1)
      .order('inserted_at', { ascending: false });
    
    if (error) throw error;
    return { data, count };
  },

  // 락업 관련 API
  getLockups: async (page = 1, limit = 15) => {
    // 먼저 락업 데이터 가져오기
    const { data: lockups, error: lockupsError, count } = await supabase
      .from(TABLES.LOCKUP)
      .select('*', { count: 'exact' })
      .range((page - 1) * limit, page * limit - 1)
      .order('inserted_at', { ascending: false });
    
    if (lockupsError) throw lockupsError;
    
    // account_id가 있는 경우 accounts 테이블에서 holder_name 가져오기
    const enrichedLockups = await Promise.all(
      lockups.map(async (lockup) => {
        if (lockup.account_id) {
          const { data: account } = await supabase
            .from(TABLES.ACCOUNTS)
            .select('holder_name, account_type')
            .eq('id', lockup.account_id)
            .single();
          
          return {
            ...lockup,
            accounts: account
          };
        }
        return {
          ...lockup,
          accounts: null
        };
      })
    );
    
    return { data: enrichedLockups, count };
  },

  getLockupByAddress: async (address) => {
    const { data, error } = await supabase
      .from(TABLES.LOCKUP)
      .select('*')
      .eq('address', address);
    
    if (error) throw error;
    return data;
  },

  // 대시보드 통계 API
  getDashboardStats: async () => {
    console.log('getDashboardStats 함수 호출됨');
    
    const { data: accounts, error: accountsError } = await supabase
      .from(TABLES.ACCOUNTS)
      .select('balance, pre_sale, kyc_yn');
    
    if (accountsError) throw accountsError;

    const { data: lockups, error: lockupsError } = await supabase
      .from(TABLES.LOCKUP)
      .select('amount, expire_yn, unlock_yn, expire_dt');
    
    if (lockupsError) throw lockupsError;
    
    // 디버깅: lockups 데이터 확인
    console.log('Lockups data:', lockups);
    console.log('Lockups count:', lockups?.length || 0);
    
    // 디버깅: accounts 데이터 확인
    console.log('Accounts data:', accounts);
    console.log('Accounts count:', accounts?.length || 0);

    // 통계 계산
    const totalAccounts = accounts?.length || 0;
    const totalSupply = 1000000000; // 고정된 Total Supply
    const totalPreSale = accounts?.reduce((sum, account) => 
      sum + parseFloat(account.pre_sale || 0), 0) || 0;
    
    // KYC 통계 계산
    const verifiedCount = accounts?.filter(account => account.kyc_yn === 'Y').length || 0;
    const anonymousCount = totalAccounts - verifiedCount;
    
    // 디버깅: KYC 통계 확인
    console.log('KYC Stats - Verified:', verifiedCount, 'Anonymous:', anonymousCount);
    const totalLockups = lockups?.length || 0;
    const totalLockedAmount = lockups?.filter(lockup => {
      // expire_dt가 현재 시각 이후인 것만 필터링
      if (!lockup.expire_dt) return false;
      const expireDate = new Date(lockup.expire_dt);
      const now = new Date();
      
      
      return expireDate > now;
    }).reduce((sum, lockup) => 
      sum + parseFloat(lockup.amount || 0), 0) || 0;
    const activeLockups = lockups?.filter(lockup => 
      lockup.expire_yn === 'N' && lockup.unlock_yn === 'N').length || 0;

    // 주별 언락 예정 수량 계산 (현재 시각 기준 4주간)
    const now = new Date();
    const weeklyUnlocks = [];
    
    for (let week = 1; week <= 4; week++) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() + (week - 1) * 7);
      weekStart.setHours(0, 0, 0, 0);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 7);
      
      // 해당 주에 만료되는 락업들의 금액 합계
      const weekUnlockAmount = lockups?.filter(lockup => {
        if (!lockup.expire_dt) return false;
        const expireDate = new Date(lockup.expire_dt);
        return expireDate >= weekStart && expireDate < weekEnd;
      }).reduce((sum, lockup) => {
        // next_unlock_amount가 있으면 사용, 없으면 amount 사용
        const unlockAmount = parseFloat(lockup.next_unlock_amount || lockup.amount || 0);
        return sum + unlockAmount;
      }, 0) || 0;
      
      weeklyUnlocks.push({
        week: `Week ${week}`,
        value: weekUnlockAmount
      });
    }

    const result = {
      totalAccounts,
      totalSupply,
      totalPreSale,
      totalLockups,
      totalLockedAmount,
      activeLockups,
      weeklyUnlocks,
      kycStats: {
        verified: verifiedCount,
        anonymous: anonymousCount
      }
    };
    
    // 디버깅: 최종 결과 확인
    console.log('Final result:', result);
    console.log('KYC Stats in result:', result.kycStats);
    
    return result;
  },

  // 관리자 관련 API
  getAdmins: async () => {
    const { data, error } = await supabase
      .from(TABLES.ADMIN)
      .select('*')
      .order('admin_level', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  checkAdminAccess: async (address) => {
    const { data, error } = await supabase
      .from(TABLES.ADMIN)
      .select('admin_level, admin_name')
      .eq('address', address)
      .single();
    
    if (error) return null;
    return data;
  },

  // 액티비티 로그 관련 API
  getActivityLogs: async (limit = 5) => {
    const { data, error } = await supabase
      .from(TABLES.ACTIVITY_LOGS)
      .select('*')
      .eq('event_name', 'Transfer')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  },

  // Foundation Wallet 잔고 조회
  getFoundationWallets: async () => {
    const foundationAddresses = [
      '0xf634f28632e348530538F3E37e4E13252A67A17A', // Treasury
      '0xB183093A00D6577400099c7cBAa15cD3437F3BED', // Private Sale
      '0xcE80026bAd6A41814259F28a86e534FFC1971489', // Liquidity
      '0xB80613E1fd3D9e3C0FC4F8D500aA96fbCD254c96'  // Team & Advisors
    ];

    const { data, error } = await supabase
      .from(TABLES.ACCOUNTS)
      .select('address, balance')
      .in('address', foundationAddresses);
    
    if (error) throw error;
    return data;
  }
};

export default supabase;
